import {
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { enGB, fr } from "date-fns/locale";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { HiCheckCircle } from "react-icons/hi";
import { MdErrorOutline } from "react-icons/md";
import { useField } from "../../../hooks/FormGeneratorHooks";
import { userAtom } from "../../../state/auth";
import { FormGroup } from "./FormGroup";
import { visteAtom } from "../../../state/visteAtom";
registerLocale("fr", fr);
registerLocale("en-GB", enGB);

// Date values are stored as ISO "YYYY-MM-DD" (what the former native
// <input type="date"> produced), but always displayed/typed as DD/MM/YYYY.
const ISO_DATE_FORMAT = "YYYY-MM-DD";
const DISPLAY_DATE_FORMAT = "dd/MM/yyyy";

const isoToDate = (value: any): Date | null => {
  if (!value) return null;
  const parsed = moment(value, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const dateToIso = (date: Date | null): string =>
  date ? moment(date).format(ISO_DATE_FORMAT) : "";

export const FieldInput = (props) => {
  const {
    errorMessage,
    id,
    isValid,
    isSubmitted,
    isValidating,
    resetKey,
    setValue,
    value,
    errorMessages,

    otherProps,
  } = useField(props);

  const { required, name } = props;
  const [user] = useAtom(userAtom);
  const { children, label, type, placeholder, helper, ...rest } = otherProps;
  const [isTouched, setIsTouched] = useState(false);
  const [isValidation, setIsValidation] = useState(false);
  const showError = !isValid && (isTouched || isSubmitted);
  const [formData] = useAtom(visteAtom);
  const { i18n } = useTranslation();
  const isFrench = (i18n.language || "").startsWith("fr");
  // Latest picked date, read on submit: onCalendarClose fires in the same
  // tick as onChange, before `value` state has re-rendered.
  const pickedDate = useRef<string | null>(null);
  const lastSubmittedDate = useRef<string | null>(null);

  useEffect(() => {
    setValue(props.value);
    pickedDate.current = null;
    lastSubmittedDate.current = props.value ?? "";
  }, [props.value, setValue]);

  useEffect(() => {
    setIsTouched(false);
  }, [resetKey]);

  const formGroupProps = {
    errorMessage,
    helper: isTouched ? helper : "",
    id,
    isRequired: !!required,
    label,
    showError,
    name,

    ...rest,
  };

  // Mirrors the onBlur submit of the plain input, once per distinct value
  // (closing the calendar and blurring the input both fire for one pick).
  const submitDate = () => {
    setIsTouched(true);
    setIsValidation(false);
    const current = pickedDate.current ?? (value ? value : (props.value ?? ""));
    if (current === lastSubmittedDate.current) return;
    lastSubmittedDate.current = current;
    if (props?.onSumbit)
      props?.onSumbit({
        fieldName: name,
        value: current,
        isValid: isValid,
      });
  };

  return (
    <FormGroup {...formGroupProps}>
      {/* { !props.isPreview && (
         <p className="text-xs text-red-300">
           {" "}
           {!isValid && !isValidation && errorMessages && errorMessages[0]}
         </p>
        )} */}
      <InputGroup>
        {rest.hasUnit && rest.hasUnit?.active && (
          <InputRightElement
            className="p-0.5 text-xs "
            fontSize={12}
            w={"auto"}
          >
            {rest.hasUnit.unit}
          </InputRightElement>
        )}

        {type === "date" ? (
          // The native date input renders its calendar and DD/MM vs MM/DD
          // order from the browser's locale, not the app's, so the same form
          // looked and parsed differently between sessions. Use a picker with
          // an explicit locale and a fixed display format instead.
          <DatePicker
            id={id}
            selected={isoToDate(value ? value : props.value)}
            onChange={(date: Date | null) => {
              setIsValidation(true);
              pickedDate.current = dateToIso(date);
              setValue(pickedDate.current);
            }}
            onCalendarClose={() => submitDate()}
            onBlur={() => submitDate()}
            dateFormat={DISPLAY_DATE_FORMAT}
            placeholderText={
              placeholder || (isFrench ? "JJ/MM/AAAA" : "DD/MM/YYYY")
            }
            locale={isFrench ? "fr" : "en-GB"}
            disabled={props.readOnly ? props.readOnly : false}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            strictParsing
            portalId="root"
            customInput={
              <Input
                borderWidth={props.readOnly ? 2 : 1}
                fontSize="14px"
                _invalid={{ borderColor: "Red" }}
                className=" focus:border-blue-500 focus:shadow-sm w-full "
                aria-invalid={showError}
                aria-describedby={!isValid ? `${id}-error` : undefined}
              />
            }
          />
        ) : (
          <Input
            borderWidth={props.readOnly ? 2 : 1}
            disabled={props.readOnly ? props.readOnly : false}
            type={type || "text"}
            id={id}
            value={value ? value : (props.value ?? "")}
            onChange={(e) => {
              setIsValidation(true);
              setValue(e.target.value);
            }}
            onBlur={(e) => {
              setIsTouched(true);
              setIsValidation(false);
              if (props?.onSumbit)
                props?.onSumbit({
                  fieldName: name,
                  value: e.target.value,
                  isValid: isValid,
                });

              //setIsTouched(false);
            }}
            fontSize="14px"
            _invalid={{ borderColor: "Red" }}
            className=" focus:border-blue-500 focus:shadow-sm w-full "
            aria-invalid={showError}
            aria-describedby={!isValid ? `${id}-error` : undefined}
            placeholder={placeholder}
          />
        )}
      </InputGroup>
      {!props?.isPreview && user?.type === "writer" && (
        <div className="absolute -left-8 top-1/2">
          {" "}
          {isValidation && (
            <p>
              <Spinner size="sm" flex="none" color="blue.200" />
            </p>
          )}
          <p>
            {" "}
            {isValid && !isValidation && (
              <HiCheckCircle color="#67d428" width={20} height={20} />
            )}
            {!isValid && !isValidation && (
              <MdErrorOutline color="#ff0000" width={20} height={20} />
            )}
          </p>
        </div>
      )}

      {children}
    </FormGroup>
  );
};
